// Gallery targets (§0.5, UX §6.6), one frame per kind of juice.json targets.byAmmoTier (bottles, cans,
// balloons, flat cardboard robots — boxy, bolt eyes). Flat cut-outs only: no animal or human likeness. Each
// is drawn in its natural TARGET_BOX (cells; drawing geometry), fitted bottom-centred into the frame; an
// even ammo tier draws the "hi" variant.
import { drawParts, fillP, type Part, part } from './passes.ts';
import { box, circle, ellipse, line, rrect } from './shapes.ts';
import type { Generator, TargetSpec } from './types.ts';

/** The target's natural box in cells (the drawing is laid out in these units). */
export const TARGET_BOX = { w: 0.3, h: 0.4 } as const;

export const targetGen: Generator<TargetSpec> = (c, spec, framePx, env) => {
  const { pal } = env;
  const W = TARGET_BOX.w * 100;
  const H = TARGET_BOX.h * 100;
  const k = Math.min(spec.w / TARGET_BOX.w, spec.h / TARGET_BOX.h);
  c.save();
  // fit: centred horizontally, standing on the frame's bottom edge
  c.translate(((spec.w - TARGET_BOX.w * k) / 2) * framePx, (spec.h - TARGET_BOX.h * k) * framePx);
  const px = framePx * k;
  c.scale(px / 100, px / 100);
  const ow = Math.max(1.6, ((env.sprites.outlineMinPx * env.dpr) / px) * 100 * 0.6);
  const cx = W / 2;
  const hi = spec.tier % 2 === 0;
  const parts: Part[] = [];
  const P = (
    path: Part['path'],
    tok: string,
    x: number,
    y: number,
    w: number,
    h: number,
    extra: Partial<Part> = {},
  ) => parts.push(part(path, pal.sw(tok), box(x, y, w, h), extra));
  if (spec.kind === 'bottle') {
    const bw = hi ? 16 : 14;
    const top = hi ? 3 : 8;
    P(rrect(cx - 3.5, top, 7, 12, 2), 'target.bottle', cx - 3.5, top, 7, 12);
    P(
      rrect(cx - bw / 2, top + 10, bw, H - top - 12, [6, 6, 3, 3]),
      'target.bottle',
      cx - bw / 2,
      top + 10,
      bw,
      H - top - 12,
      {
        detail: (cc) => {
          if (hi) fillP(cc, rrect(cx - bw / 2, top + 20, bw, 8, 0), pal.hex('ui.cream2'));
          fillP(
            cc,
            rrect(cx - bw / 2 + 2.4, top + 13, 2.4, H - top - 20, 1.2),
            pal.sw('target.bottle').hi,
          );
        },
      },
    );
    P(rrect(cx - 4.2, top - 2, 8.4, 4, 1.5), 'ui.cream2', cx - 4.2, top - 2, 8.4, 4);
  } else if (spec.kind === 'can') {
    const cw = hi ? 22 : 18;
    const ch = hi ? 30 : 24;
    const y0 = H - ch - 3;
    P(rrect(cx - cw / 2, y0, cw, ch, 3), 'target.can', cx - cw / 2, y0, cw, ch, {
      detail: (cc) => {
        fillP(cc, rrect(cx - cw / 2, y0 + ch * 0.36, cw, ch * 0.28, 0), pal.hex('ui.cream'));
        fillP(cc, ellipse(cx, y0 + 1.6, cw / 2 - 1, 1.6), pal.sw('material.steel').base);
        if (hi) fillP(cc, circle(cx, y0 + ch * 0.5, 2.6), pal.hex('target.can'));
      },
    });
  } else if (spec.kind === 'balloon') {
    const tok = hi ? pal.tier(spec.tier) : 'target.balloon';
    const rx = 12;
    const ry = 14;
    const cy = 16;
    P(ellipse(cx, cy, rx, ry), tok, cx - rx, cy - ry, rx * 2, ry * 2, {
      detail: (cc) => {
        fillP(cc, ellipse(cx - 4.5, cy - 6, 3, 4.2), pal.sw(tok).hi);
      },
    });
    P(
      (cc) => {
        cc.moveTo(cx - 3, cy + ry + 2.5);
        cc.lineTo(cx + 3, cy + ry + 2.5);
        cc.lineTo(cx, cy + ry - 1);
        cc.closePath();
      },
      tok,
      cx - 3,
      cy + ry - 1,
      6,
      3.5,
      {
        detail: (cc) => {
          cc.beginPath();
          line([
            [cx, cy + ry + 2.5],
            [cx - 2.5, cy + ry + 7],
            [cx + 1.5, H - 2],
          ])(cc);
          cc.strokeStyle = pal.hex('ui.cream');
          cc.lineWidth = 1.2;
          cc.stroke();
        },
      },
    );
  } else {
    // cardboard robot: boxy head, bolt eyes, flat body on a stand
    const rw = hi ? 24 : 20;
    const hy = hi ? 5 : 9;
    const card = pal.sw('target.robot');
    P(rrect(cx - rw / 2, hy + 13, rw, 16, 2), 'target.robot', cx - rw / 2, hy + 13, rw, 16, {
      detail: (cc) => {
        fillP(cc, rrect(cx - 4, hy + 17, 8, 7, 1.5), card.shade);
        fillP(cc, circle(cx, hy + 20.5, 1.6), pal.hex('ui.money'));
      },
    });
    P(
      rrect(cx - rw / 2 + 3, hy, rw - 6, 13, 2.5),
      'target.robot',
      cx - rw / 2 + 3,
      hy,
      rw - 6,
      13,
      {
        detail: (cc) => {
          for (const d of [-1, 1]) {
            fillP(cc, circle(cx + d * 4.2, hy + 6, 2.6), pal.hex('material.steel'));
            fillP(cc, circle(cx + d * 4.2, hy + 6, 1.1), card.outline);
          }
          fillP(cc, rrect(cx - 4, hy + 9.5, 8, 1.8, 0.9), card.outline);
        },
      },
    );
    P(rrect(cx - 2, hy + 29, 4, H - hy - 31, 1), 'ui.woodDark', cx - 2, hy + 29, 4, H - hy - 31);
    if (hi) P(circle(cx, hy - 1.5, 2.6), 'tier.8', cx - 2.6, hy - 4.1, 5.2, 5.2);
  }
  drawParts(c, parts, {
    ow,
    rim: false,
    halo: null,
    glow: null,
    shadeBand: pal.rules.shadeBand,
    hiW: 1.4,
  });
  c.restore();
};
